import { isBefore } from 'date-fns';

import { MAX_DEFAULT_MESSAGE_LENGTH } from 'constants/InterventionConstants';

import { EMAIL_VALIDATION_REGEX } from 'constants/UrlConstants';

import { isNilOrEmpty } from '../../services/Utils';

const PASSWORD_MIN_LENGTH = /^\S{14,}$/;

const PASSWORD_SYMBOLS = /[\^$*.\]\[{}\)\(?"!@#%&\/\\,><':;|_~`=+-]/;

const PASSWORD_NUMBERS = /[0-9]/;

const PASSWORD_UPPERCASE = /(?=.*?[A-Z])(?=.*?[a-z])/;

export const SENDER_ID_MAX_LENGTH = 11;

export const SHORTNAME_MAX_LENGTH = 20;

/** A translation key when the value is rejected, `undefined` when it passes. */
export type ValidationError = string | undefined;

/**
 * One react-final-form validator. `allValues` and `meta` are forwarded untouched by
 * `composeValidations`, so a validator that ignores them stays assignable.
 */
export type Validator<V = unknown> = (value: V, allValues?: unknown, meta?: unknown) => ValidationError;

/**
 * In charge of form (RFF) validation.
 * Validation functions can be called individually, or mixed.
 * In this case, you have to call them via composeValidations: for example `composeValidations(required, validateVIN)`.
 */
export const composeValidations =
  <V>(...validations: Array<Validator<V>>): Validator<V> =>
    (value, allValues, meta) =>
      validations.reduce<ValidationError>((error, validator) => error || validator(value, allValues, meta), undefined);

export const REFERENCE_ID_PATTERN = /^[\p{L}\p{N} !@#$&()\-`.+,/"_=*?'{}:]{1,80}$/u;

export const required: Validator = value => (!value && typeof value !== 'boolean' ? 'validation.required' : undefined);

export const requiredAndNotEmptyString: Validator<string | null | undefined> = value => {
  const trimmed = value?.trim() ?? '';
  return !isNilOrEmpty(trimmed) ? undefined : 'validation.required';
};

export const maxLengthString = (maxLength: number = MAX_DEFAULT_MESSAGE_LENGTH): Validator<string | undefined> => (value = '') =>
  (value?.trim()?.length > maxLength ? 'validation.maxlength' : undefined);

export const requiredAndMaxLengthString =
  (maxLength: number = MAX_DEFAULT_MESSAGE_LENGTH): Validator<string | null | undefined> =>
    value => {
      const trimmed = value?.trim() ?? '';

      if (isNilOrEmpty(trimmed)) {
        return 'validation.required';
      }

      if (trimmed.length > maxLength) {
        return 'validation.maxlength';
      }

      return undefined;
    };

export const hasUniqValueArray = (value: unknown, arr: unknown[] = []): ValidationError => {
  const isUniq = arr.filter(arrayValue => arrayValue === value).length === 1;
  return !isUniq ? 'validation.uniq' : undefined;
};

export const requiredAndNotEmptyArray: Validator<unknown[] | undefined> = (value = []) => (value.length ? undefined : 'validation.required');

export const maxArraySize = (array: unknown[] = [], size = 5): ValidationError => (array?.length > size ? 'validation.maxArraySize' : undefined);

export const requiredAndNotEmptyLabel: Validator<Record<string, unknown> | undefined> = (value = {}) =>
  (Object.values(value).filter(Boolean).length > 0 ? undefined : 'validation.required');

export const requiredAndPositiveNumber: Validator<number> = value =>
  (Number.isNaN(value) || value < 0 ? 'validation.number' : undefined);

export const validateVIN: Validator<string> = vin => {
  const pattern = /^([A-HJ-NPR-Za-hj-npr-z\d]{0,18})?$/;
  return !vin || pattern.test(vin) ? undefined : 'validation.vin';
};

/** Unlike its siblings this one answers a boolean, not a translation key. */
export const validateReferenceId = (referenceId: string): boolean => {
  const pattern = RegExp(REFERENCE_ID_PATTERN);
  return !referenceId || pattern.test(referenceId);
};

export const validateEmail: Validator<string> = email => {
  const pattern = RegExp(EMAIL_VALIDATION_REGEX);
  return !email || pattern.test(email) ? undefined : 'page.user.error.email.invalid';
};

export const isEmailValid = (email: string): boolean => EMAIL_VALIDATION_REGEX.test(email);

export const requiredBoolean: Validator = value => (typeof value === 'boolean' ? undefined : 'validation.required');

export const PASSWORD_VALIDATION_LIST = {
  minLength: PASSWORD_MIN_LENGTH,
  minSymbol: PASSWORD_SYMBOLS,
  minNumber: PASSWORD_NUMBERS,
  minUppercase: PASSWORD_UPPERCASE,
};

export const validatePattern = (pattern: RegExp, error = 'validation.regexp'): Validator<string> => value =>
  (Boolean(value) && !value.match(pattern) ? error : undefined);

export const HTTPSUrlValidator = validatePattern(/^https:\/\/(.*)/, 'validation.https');

export const senderIdValidator: Validator<string> = value =>
  (value
    ? validatePattern(/^[\w]*[A-Za-z][\w ]*$/, 'validation.senderId')(value) ??
          requiredAndMaxLengthString(SENDER_ID_MAX_LENGTH)(value)
    : undefined);

export const strictSenderIdValidator: Validator<string> = value =>
  (value
    ? validatePattern(/^[a-zA-Z0-9]*$/, 'validation.senderId')(value) ??
    requiredAndMaxLengthString(SENDER_ID_MAX_LENGTH)(value)
    : undefined);

export const shortNameValidator: Validator<string> = value =>
  (value ? requiredAndMaxLengthString(SHORTNAME_MAX_LENGTH)(value) : undefined);

export const dateIsAfter = (date: Date | number): Validator<Date | number> => value =>
  (value && isBefore(value, date) ? 'validation.dateIsAfter' : undefined);

export const notNullOrUndefined: Validator = value => ((value === null || value === undefined) ? 'validation.required' : undefined);
